import React, {Dispatch, SetStateAction, useEffect, useRef, useState} from "react";
import {
    APIProvider,
    ControlPosition,
    Map,
    MapCameraChangedEvent,
    Marker,
    useMap,
    useMapsLibrary
} from "@vis.gl/react-google-maps";
import {BARRECA_CENTER} from "./AutocompleteAddressInput";
import {google_api_key} from "../../services/common.services";

class MapDrawingProps {
    handleCameraChange: (ev: MapCameraChangedEvent) => void
    location: { lat: number, lng: number }
    defaultZoom?: number
    mapTypeId?: string
    allowControl?: boolean
    handlePolygon: (poly: google.maps.Polygon) => void
    defaultCenter?: { lat: number, lng: number }
    onLoad?: () => void;
    defaultPolygonPath?:  google.maps.LatLngLiteral[]
}

const MapWithDrawing: React.FC<MapDrawingProps> = ({handleCameraChange, defaultPolygonPath, location, onLoad, defaultZoom = 21, mapTypeId = 'hybrid', allowControl = true, handlePolygon, defaultCenter = BARRECA_CENTER}) => {
    const [newPolygonMade, setNewPolyMade] = useState(false);
    return (
        <APIProvider apiKey={google_api_key} libraries={['places','drawing','geometry']} onLoad={onLoad}>
            <Map defaultZoom={defaultZoom} defaultCenter={defaultCenter} style={{ width: '100%', height: '100%' }}
                 mapTypeId={mapTypeId}
                 mapTypeControl={false}
                 mapTypeControlOptions={{
                     position: 23,
                     style: 2
                 }}
                 onCameraChanged={handleCameraChange}
            >
                <Marker position={location}/>
                <DrawingManager defaultPolygonPath={defaultPolygonPath} onPolygonComplete={handlePolygon} newPolygonMade={newPolygonMade} setNewPolygonMade={setNewPolyMade}/>
                <PanTo location={location}/>
            </Map>
        </APIProvider>
    );
}

export default MapWithDrawing;

const DrawingManager: React.FC<{
    onPolygonComplete: (poly: google.maps.Polygon) => void;
    defaultPolygonPath?: google.maps.LatLngLiteral[];
    setNewPolygonMade: Dispatch<SetStateAction<boolean>>;
    newPolygonMade: boolean;
}> = ({ onPolygonComplete, defaultPolygonPath, setNewPolygonMade, newPolygonMade }) => {
    const map = useMap();
    const drawingLib = useMapsLibrary('drawing');
    const polygonRef = useRef<google.maps.Polygon | null>(null);
    const drawingManagerRef = useRef<google.maps.drawing.DrawingManager | null>(null);

    const enableDrawingMode = () => {
        if (drawingManagerRef.current) {
            drawingManagerRef.current.setDrawingMode(drawingLib.OverlayType.POLYGON);
        }
    };

    const setupPolygon = (poly: google.maps.Polygon) => {
        polygonRef.current = poly;
        setNewPolygonMade(true);
        onPolygonComplete(poly);
        const path = poly.getPath();
        const changeListeners = ['set_at', 'insert_at', 'remove_at'].map(evt =>
            path.addListener(evt, () => onPolygonComplete(poly))
        );
        const rightClickListener = poly.addListener('rightclick', (e: google.maps.PolyMouseEvent) => {
            if (e.vertex != null) {
                onPolygonComplete(poly);
                if (path.getLength() < 3) {
                    poly.setMap(null);
                    polygonRef.current = null;
                    setNewPolygonMade(false);
                    enableDrawingMode();
                }
            }
        });

        return () => {
            changeListeners.forEach(l => l.remove());
            rightClickListener.remove();
            poly.setMap(null);
        };
    };

    const hasInitializedRef = useRef(false);

    useEffect(() => {
        if (!map || !drawingLib) return;

        if (!hasInitializedRef.current && defaultPolygonPath && defaultPolygonPath.length > 0) {
            hasInitializedRef.current = true;
            const defaultPoly = new google.maps.Polygon({
                paths: defaultPolygonPath,
                editable: true,
                draggable: false,
                fillColor: '#00FFFF',
                fillOpacity: 0.4,
                strokeWeight: 2,
            });
            defaultPoly.setMap(map);
            setupPolygon(defaultPoly);
        }

        const mgr = new drawingLib.DrawingManager({
            drawingControl: !newPolygonMade,
            drawingMode: null,
            drawingControlOptions: {
                position: ControlPosition.TOP_CENTER,
                drawingModes: [drawingLib.OverlayType.POLYGON],
            },
            polygonOptions: {
                editable: true,
                draggable: false,
                fillColor: '#00FFFF',
                fillOpacity: 0.4,
                strokeWeight: 2,
            },
        });

        drawingManagerRef.current = mgr;
        mgr.setMap(map);

        const handleNew = mgr.addListener('polygoncomplete', (poly: google.maps.Polygon) => {
            if (newPolygonMade || polygonRef.current) {
                poly.setMap(null);
                return;
            }

            mgr.setDrawingMode(null);
            setupPolygon(poly);
        });

        return () => {
            handleNew.remove();
            mgr.setMap(null);
        };
    }, [map, drawingLib, newPolygonMade, defaultPolygonPath]);

    return null;
};


function PanTo({ location }: { location: { lat: number; lng: number } | null }) {
    const map = useMap();
    useEffect(() => {
        if (map && location) {
            map.panTo(location);
            map.setZoom(21);
        }
    }, [map, location]);

    return null;
}
